import { Component, OnInit, DestroyRef, inject, signal, ViewChildren, QueryList } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatAccordion } from '@angular/material/expansion';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';

import { Child } from '@app/models/child';
import { CarnetEntry, ConfigCarnetLiaison, FirstDA } from '@app/models/carnet-liaison';
import { BaseConfigService, ChildService, FamilyService } from '@app/services';
import { CarnetLiaisonService } from '@app/services/carnet-liaison.service';

@Component({
  selector: 'app-carnet-liaison',
  templateUrl: './carnet-liaison.component.html',
  styleUrl: './carnet-liaison.component.scss',
  standalone: false
})
export class CarnetLiaisonComponent implements OnInit {

  @ViewChildren(MatAccordion) accordions!: QueryList<MatAccordion>;

  allExpanded = false;

  children: Child[] | null = null;
  selectedChild: Child | null = null;
  selectedDate: Date = new Date();
  entry: CarnetEntry | null = null;
  firstDAOnly: FirstDA | null = null;
  config: ConfigCarnetLiaison | null = null;
  isLoading = false;
  hasNoEntry = false;
  readonly today = new Date();

  private destroyRef = inject(DestroyRef);
  private navigationTrigger = signal(0);
  private navigationTrigger$ = toObservable(this.navigationTrigger);

  private childService = inject(ChildService);
  private familyService = inject(FamilyService);
  private carnetService = inject(CarnetLiaisonService);
  private configService = inject(BaseConfigService);

  ngOnInit(): void {
    this.configService.get<ConfigCarnetLiaison>('carnetLiaison').subscribe(config => {
      this.config = config;
    });

    this.navigationTrigger$.pipe(
      filter(v => v > 0),
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.loadEntry());

    this.familyService.currentFamily$.pipe(
      filter(f => !!f),
      distinctUntilChanged((x, y) => x?.id === y?.id),
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        this.children = null;
        this.selectedChild = null;
        this.entry = null;
        this.hasNoEntry = false;
      }),
      switchMap(f => this.childService.getFamilyChildren(f))
    ).subscribe(children => {
      this.children = children;
      if (children?.length === 1) {
        this.selectChild(children[0]);
      }
    });
  }

  selectChild(child: Child): void {
    this.selectedChild = child;
    this.navigationTrigger.update(v => v + 1);
  }

  prevDay(): void {
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() - 1);
    this.selectedDate = d;
    this.navigationTrigger.update(v => v + 1);
  }

  nextDay(): void {
    const d = new Date(this.selectedDate);
    d.setDate(d.getDate() + 1);
    this.selectedDate = d;
    this.navigationTrigger.update(v => v + 1);
  }

  onDateChange(date: Date): void {
    if (date) {
      this.selectedDate = date;
      this.navigationTrigger.update(v => v + 1);
    }
  }

  get isNextDayDisabled(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sel = new Date(this.selectedDate);
    sel.setHours(0, 0, 0, 0);
    return sel >= today;
  }

  toggleAllPanels(): void {
    if (this.allExpanded) {
      this.accordions.forEach(a => a.closeAll());
    } else {
      this.accordions.forEach(a => a.openAll());
    }
    this.allExpanded = !this.allExpanded;
  }

  getPanelStyle(blockKey: string): { [key: string]: string } {
    const entry = (this.config?.transmission as any)?.[blockKey] ?? (this.config?.developpement as any)?.[blockKey];
    if (!entry?.color) return {};
    return {
      '--sec': entry.theme ? `var(--${entry.theme})` : entry.color,
    };
  }

  private loadEntry(): void {
    if (!this.selectedChild || !this.familyService.currentFamily) return;

    const idEnfant = this.selectedChild.id;
    const raw = this.selectedDate as any;
    const d: Date = raw instanceof Date ? raw : (raw.toDate ? raw.toDate() : new Date(raw));
    const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;

    this.isLoading = true;
    this.entry = null;
    this.firstDAOnly = null;
    this.hasNoEntry = false;

    this.carnetService.getEntry(idEnfant, dateStr).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entry => {
      this.isLoading = false;
      if (entry?.idCDL) {
        this.entry = entry;
      } else {
        const fd = entry?.FirstDA ?? null;
        const hasContent = !!(
          fd?.DA_devPsychomoteur?.length ||
          fd?.DA_acquisitionLangage?.length ||
          fd?.DA_diversification?.length ||
          fd?.DA_devPhysiquePhysiologique?.length
        );
        this.firstDAOnly = hasContent ? fd : null;
        this.hasNoEntry = !hasContent;
      }
    });
  }
}
